import * as React from 'react';
import { Article, ArticleChangeImportance, ArticleContact, defaultArticlePublishedEmailTemplate, fieldValidation, ReleaseRecord } from '../../../../../amorg-common';
import { articlesClientApi } from '../../../../../api/articlesClientApi';
import { ImportanceAvatar } from '../../../../../designSystem/avatar/ImportanceAvatar';
import { Button } from '../../../../../designSystem/button/Button';
import { ButtonRow } from '../../../../../designSystem/button/ButtonRow';
import { colors } from '../../../../../designSystem/colors/color';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { FormField } from '../../../../../designSystem/formField/FormField';
import { TextAreaSaveField } from '../../../../../designSystem/formField/textArea/TextAreaSaveField';
import { Checkbox } from '../../../../../designSystem/inputs/Checkbox';
import { HtmlEditorWithSave } from '../../../../../designSystem/inputs/HtmlEditorWithSave';
import { LoadingPageOverlay } from '../../../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../../../designSystem/MessageCard';
import { Modal } from '../../../../../designSystem/Modal';
import { Small } from '../../../../../designSystem/typography/Body';
import { H2 } from '../../../../../designSystem/typography/Heading';
import { ArticleViewImportanceSelector } from './ArticleViewImportanceSelector';
import { Table } from '../../../../../designSystem/Table';
import { ToggletipTheOne } from '../../../../../designSystem/ToggletipTheOne';
import { TooltipTheOneWrapper } from '../../../../../designSystem/TooltipTheOne';
import { Em } from '../../../../../designSystem/typography/Em';

type ArticleViewReleasePanelProps = {
    article: Article;
    releaseRecords: ReleaseRecord[];
    contacts: ArticleContact[];
    onChanged: () => void;
};

export function ArticleViewReleasePanel (props: ArticleViewReleasePanelProps): JSX.Element
{
    const sortedReleaseRecords = React.useMemo(() => 
        [...props.releaseRecords].sort((a, b) => a.subscriber.siteId.localeCompare(b.subscriber.siteId)), 
    [props.releaseRecords]);

    return (

        <Flex flexDirection="column" gap={16}>
            <H2>Udgivelser</H2>
            <Table>
                <thead>
                    <tr>
                        <th>Site</th>
                        <th style={{ textAlign: 'center' }}>Betydning</th>
                        <th style={{ textAlign: 'center' }}>Ændringsbeskrivelse</th>
                        <th style={{ textAlign: 'center' }}>Vurdering</th>
                        <th style={{ textAlign: 'center' }}>Email-skabelon</th>
                        <th>Kontaktpersoner</th>
                        <th>Tilpasning</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedReleaseRecords.map((record) => {
                        const contacts = props.contacts.filter(c => c.subscriberId === record.subscriber.id);
                        return (
                            <tr key={record.id}>
                                <td>{record.subscriber.siteId}</td>
                                <td>
                                    {record.customImportance && record.customImportance !== props.article.importance
                                        ?   <Flex gap={4} alignItems='center'>
                                                <ImportanceAvatar importance={record.customImportance} />
                                                <CustomizedFlagIcon />
                                            </Flex>
                                        :   <ImportanceAvatar importance={props.article.importance} />
                                    }
                                </td>
                                <td style={{ textAlign: 'center' }}>{record.customChangeDescription 
                                    ? <CustomizedFlagIcon />
                                    : <GenericFlagIcon />
                                }</td>
                                <td style={{ textAlign: 'center' }}>{record.customAssessment
                                    ? <CustomizedFlagIcon />
                                    : <GenericFlagIcon />
                                }</td>
                                <td style={{ textAlign: 'center' }}>{record.customEmailTemplate
                                    ? <CustomizedFlagIcon />
                                    : <GenericFlagIcon />
                                }</td>
                                <td>
                                    {contacts.length > 0
                                        ?   <TooltipTheOneWrapper tooltipContent={contacts.map(c => (
                                                <div key={c.email}><Small>{c.email}</Small></div>
                                            ))}>
                                                <span><Em>{contacts.length}</Em> kontaktpersoner</span>
                                            </TooltipTheOneWrapper>
                                        :   <span>Ingen kontaktpersoner</span>
                                    }
                                </td>
                                <td>
                                    <EditReleaseModal
                                        siteId={record.subscriber.siteId}
                                        release={record}
                                        readOnly={props.article.release !== null}
                                        onChanged={props.onChanged} />
                                </td>
                            </tr>
                        );
                    })}

                    {sortedReleaseRecords.length === 0 && 
                        <tr><td colSpan={7}>Der er ingen udgivelser</td></tr>
                    }
                </tbody>
            </Table>
        </Flex>
    );
}

function CustomizedFlagIcon(): JSX.Element
{
    return (
        <ToggletipTheOne display="inline-flex" icon='warning' iconColor={colors.icon.warning}>
            <span>Tilpasset kunden</span>
        </ToggletipTheOne>
    );
}

function GenericFlagIcon(): JSX.Element
{
    return (
        <ToggletipTheOne display="inline-flex" icon='data_files_document' iconColor={colors.icon.passive}>
            <span>Standard</span>
        </ToggletipTheOne>
    );
}


type EditReleaseModalButtonProps = {
    siteId: string,
    release: ReleaseRecord,
    readOnly: boolean,
    onChanged: () => void;
};
export function EditReleaseModal (props: EditReleaseModalButtonProps): JSX.Element
{
    const [show, setShow] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState(false);

    async function handleChangeImportance (importance: ArticleChangeImportance)
    {
        if (props.readOnly)
            return;

        setLoading(true);
        await articlesClientApi.updateReleaseRecord(
            props.release.id,
            props.release.customChangeDescription,
            props.release.customAssessment,
            importance,
            props.release.customEmailTemplate
        );
        props.onChanged();
        setLoading(false);
    }

    async function handleUpdate (customChangeDescription: string, customAssessment: string, isValid: boolean)
    {
        if (props.readOnly || !isValid)
            return;

        setLoading(true);
        await articlesClientApi.updateReleaseRecord(
            props.release.id,
            customChangeDescription,
            customAssessment,
            props.release.customImportance,
            props.release.customEmailTemplate
        );
        props.onChanged();
        setLoading(false);
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />
            <Button
                variant='outlined'
                icon={props.readOnly ? 'interface_actions_view' : 'interface_actions_edit'}
                text={props.readOnly ? 'vis' : 'tilpas'}
                size='32px'
                onClick={() => setShow(true)} />
            <Modal showModal={show} title={`${props.readOnly ? 'Vis' : 'Rediger'} udgivelse for ${props.siteId}`} onCancel={() => setShow(false)}
                content={
                    <>
                        <Flex flexDirection='column' gap={16}>
                            <Flex gap={16}>
                                <div style={{ flexGrow: 1 }}>
                                    <ArticleViewImportanceSelector
                                        groupName='customImportance'
                                        importance={props.release.customImportance}
                                        disabled={props.readOnly}
                                        onImportanceChange={handleChangeImportance} />
                                </div>
                            </Flex>

                            <EmailTemplateField record={props.release} readOnly={props.readOnly} onChanged={props.onChanged} />

                            {props.readOnly
                                ? <>
                                    <FormField
                                        label='Tilpasset ændringsbeskrivelse'
                                        inputComponent={<div style={{ whiteSpace: 'pre-wrap' }}>{props.release.customChangeDescription}</div>} />
                                    <FormField
                                        label='Tilpasset vurdering'
                                        inputComponent={<div style={{ whiteSpace: 'pre-wrap' }}>{props.release.customAssessment}</div>} />
                                </>
                                : <>
                                    <TextAreaSaveField
                                        label='Tilpasset ændringsbeskrivelse'
                                        minRows={3}
                                        value={props.release.customChangeDescription}
                                        validator={(value) =>
                                            fieldValidation.articleReleaseRecord.customChangeDescription(value)
                                                ? { valid: true }
                                                : {
                                                    valid: false, errorMessage: `Maks. 5000 tegn (${value.length})`
                                                }}
                                        onSave={(value, isValid) => handleUpdate(value, props.release.customAssessment, isValid)} />

                                    <TextAreaSaveField
                                        label='Tilpasset vurdering'
                                        minRows={3}
                                        value={props.release.customAssessment}
                                        validator={(value) =>
                                            fieldValidation.articleReleaseRecord.customAssessment(value)
                                                ? { valid: true }
                                                : {
                                                    valid: false, errorMessage: `Maks. 5000 tegn (${value.length})`
                                                }}
                                        onSave={(value, isValid) => handleUpdate(props.release.customChangeDescription, value, isValid)} />
                                </>}

                            <ButtonRow>
                                <Button variant='outlined' text='Luk' onClick={() => setShow(false)} />
                            </ButtonRow>
                        </Flex>
                    </>
                } />
        </>
    );
}

type EmailTemplateFieldProps = {
    record: ReleaseRecord,
    readOnly: boolean,
    onChanged: () => void;
};
function EmailTemplateField (props: EmailTemplateFieldProps): JSX.Element
{
    const [customized, setCustomized] = React.useState<boolean>(false);
    const [error, setError] = React.useState<boolean>(false);

    React.useEffect(() =>
    {
        setCustomized(props.record.customEmailTemplate !== '');
    }, [props.record.customEmailTemplate]);



    async function handleCustomizedCheckedChange (value: string, checked: boolean)
    {
        if (props.readOnly)
            return;

        if (checked)
        {
            await onSaveEmail(defaultArticlePublishedEmailTemplate);
        }
        else 
        {
            await onSaveEmail('');
        }
    }

    async function onSaveEmail (content: string): Promise<boolean>
    {
        if (props.readOnly)
            return false;

        const result = await articlesClientApi.updateReleaseRecord(
            props.record.id,
            props.record.customChangeDescription,
            props.record.customAssessment,
            props.record.customImportance,
            content
        );
        if (result.status === 'success')
        {
            props.onChanged();
            setError(false);
            return true;
        }
        else
        {
            setError(true);
            return false;
        }
    }

    const tagReplacementToggleTip =
        <span>
            <p>Der kan benyttes følgende erstatningstekst i feltet:</p>

            <p>
                <b>{'{company.name}'}</b><br />
                Indsætter organisationens fulde navn hvis defineret. Ellers organisationens id.<br />
                <b>{'{contactperson.firstname}'}</b><br />
                Indsætter fornavn på modtageren.<br />
                <b>{'{contactperson.fullname}'}</b><br />
                Indsætter fulde navn på modtageren.<br />
                <b>{'{article.title}'}</b><br />
                Indsætter lovkravets titel.<br />
                <b>{'{article.listname}'}</b><br />
                Indsætter navnet på den liste lovkravet er tilknyttet.<br />
                <b>{'{article.url}'}</b><br />
                Indsætter URL til lovkravet.<br />
            </p>
        </span>;

    return (
        <div>
            {error &&
                <MessageCard variant='error'>
                    Kunne ikke opdateret e-mail teksten. Kontrollér at teksten ikke overstiger 5000 tegn, og at alle erstatningsfelter er korrekt udformet.
                </MessageCard>
            }
            <FormField
                label="Tilpasset e-mail skabelon"
                toolTip={tagReplacementToggleTip}
                inputComponent={
                    <HtmlEditorWithSave
                        id={props.record.id + '_email_template_editor'}
                        value={props.record.customEmailTemplate}
                        onSave={onSaveEmail}
                        onCancel={() => setError(false)}
                        readonly={props.readOnly || !customized} />
                }
            />

            <Checkbox text='Tilpasset email skabelon' value="customized"
                checked={customized}
                disabled={props.readOnly}
                onChange={handleCustomizedCheckedChange} />
        </div>
    );
}
